import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { SnackbarProvider, closeSnackbar } from 'notistack';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LoginPage from './LoginPage';
import Dashboard from './Dashboard';
import ProtectedRoute from './ProtectedRoute';
import PublicRoute from './PublicRoute';
import TwoFactorAuthentication from './TwoFactorAuthentication';
import AuthSessionManager from './AuthSessionManager';
import ViewUpdates, { hasUnseenWhatsNew } from './views/ViewUpdates';
import PDFManipulation from './views/PDFManipulation';
import ResetPasswordPage from './views/ResetPasswordPage';
// import FurnitureTesting from './views/FurnitureTesting'; // DISABLED - Interface hidden
import Protected2FARoute from './Protected2FARoute';
// import ProductBuilding from './views/ProductBuilding'; // DISABLED - Interface hidden
import ExcelReports from './views/ExcelReports';
import SAF1909InventoryAnalysis from './views/SAF1909InventoryAnalysis';
import RemoveBackground from './views/RemoveBackground';
import PriceScraper from './views/PriceScraper';
// import FTPTransfer from './views/FTPTransfer'; // DISABLED - Interface hidden
import MarketingCopies from './views/MarketingCopies';
// import InactiveItems from './views/InactiveItems'; // DISABLED - Interface hidden
import StateFreight from './views/StateFreight';
import CanadianFreight from './views/CanadianFreight';
import EUFreight from './views/EUFreight';
import ReportsScheduler from './views/ReportsScheduler';
import DocumentTranslation from './views/DocumentTranslation';
import ProductDetails from './views/ProductDetails';
import ExportImages from './views/ExportImages';
import ItemImageGrouping from './views/ItemImageGrouping';
import AWSImageLinks from './views/AWSImageLinks';
// import AWSImageLinksJeff from './views/AWSImageLinksJeff';
// import AWSImageLinksJeffFurniture from './views/AWSImageLinksJeffFurniture';
import AWSImageLinksAlternate from './views/AWSImageLinksAlternate';
import CollectionImageLinks from './views/CollectionImageLinks';
import WayfairAssetUpload from './views/WayfairAssetUpload';
import AmazonBulkImageUploader from './views/AmazonBulkImageUploader';
import BrokenLinkChecker from './views/BrokenLinkChecker';
import SocialMediaContent from './views/SocialMediaContent';
import ProductDataValidator from './views/ProductDataValidator';
import SEOContentWriter from './views/SEOContentWriter';
import EmailCampaignBuilder from './views/EmailCampaignBuilder';
import BarcodeGenerator from './views/BarcodeGenerator';
import CustomerStyles from './views/CustomerStyles';
import ItemsDetail from './views/ItemsDetail';
import ExcelLanding from './views/category-landing/ExcelLanding';
import PDFLanding from './views/category-landing/PDFLanding';
import LinksLanding from './views/category-landing/LinksLanding';
import OthersLanding from './views/category-landing/OthersLanding';
import AILanding from './views/category-landing/AILanding';
import ReportsLanding from './views/category-landing/ReportsLanding';
import AIImageGenerator from './views/AIImageGenerator';
import AIVideoGenerator from './views/AIVideoGenerator';
import AIImageSearch from './views/AIImageSearch';
import RugRoomShots from './views/RugRoomShots';
import AmazonRugTitles from './views/AmazonRugTitles';
import DashboardHome from './views/DashboardHome';
import SalesPerformanceDashboard from './views/analytics/SalesPerformanceDashboard';
import SalesDashboardV2 from './views/SalesDashboardV2';
import ReturnsDashboard from './views/ReturnsDashboard';
import RetailOS from './views/RetailOS';
import NewItems from './views/NewItems';
import CustomerStyleGaps from './views/CustomerStyleGaps';
import CollectionLaunches from './views/CollectionLaunches';
import SalesPulse from './views/SalesPulse';
import ReorderConsole from './views/ReorderConsole';
import HomeGoods from './views/dashboard/HomeGoods';
import PalletLabels from './views/dashboard/PalletLabels';
import PDQLabels from './views/dashboard/PDQLabels';
import PODistribution from './views/PODistribution';
import PODistributionWHS from './views/PODistributionWHS';
import PalletOptimizer from './views/PalletOptimizer';
import BilingualLabels from './views/BilingualLabels';
import AMA9999UCCLabels from './views/AMA9999UCCLabels';
import SafaviehInvoices from './views/SafaviehInvoices';
import CartonLabels from './views/CartonLabels';
import AmazonLabelSeparator from './views/AmazonLabelSeparator';
import PDFtoExcel from './views/PDFtoExcel';
import CADInvoices from './views/CADInvoices';
import MasterRetailRugs from './views/MasterRetailRugs';
import LabelPrinting from './views/LabelPrinting';
import SalesOrderCreation from './views/SalesOrderCreation';
import SalesDashboard from './views/SalesDashboard';
import AssetExplorer from './views/AssetExplorer';
import BulkImageDownloader from './views/BulkImageDownloader';
import FileConverter from './views/FileConverter';
import { NotificationProvider } from './context/NotificationContext';
import { ProcessProvider } from './context/ProcessContext';
import { FavoritesProvider } from './context/FavoritesContext';
import { ActivityLogProvider } from './context/ActivityLogContext';
import { PermissionProvider } from './context/PermissionContext';
import ActivityLogPanel from './components/ActivityLogPanel';
import UserManagement from './views/admin/UserManagement';
import RoleManagement from './views/admin/RoleManagement';
import NotificationManagement from './views/admin/NotificationManagement';
import FileHistoryAdmin from './views/admin/FileHistoryAdmin';
import ProtectedInterfaceRoute from './ProtectedInterfaceRoute';
import WorkspacePage from './views/workspace/WorkspacePage';
import ProfilePage from './views/workspace/ProfilePage';
import TasksPage from './views/workspace/TasksPage';
import PasswordGeneratorPage from './views/workspace/PasswordGeneratorPage';

// "What's New"-aware landing. Each time a user lands on /dashboard:
//   - if there's at least one recent + unseen What's New entry → redirect
//     them to /dashboard/view-updates once per browser session
//   - otherwise → render the normal DashboardHome
//
// This naturally covers: new entry just shipped (their seen-set is missing
// the new id), brand new browser / device (empty seen-set so every recent
// entry counts as unseen), 2FA on a new device, account just created. Once
// they view view-updates, the seen-set updates and subsequent /dashboard
// hits in the same session render DashboardHome.
//
// The session flag prevents loops if a user clicks "Home" repeatedly before
// the seen-set has caught up — same browser session = at most one redirect.
const DashboardLanding = () => {
  const SESSION_KEY = 'dashboard_redirected_this_session';
  const alreadyRedirected = sessionStorage.getItem(SESSION_KEY) === '1';
  if (!alreadyRedirected && hasUnseenWhatsNew()) {
    sessionStorage.setItem(SESSION_KEY, '1');
    return <Navigate to="/dashboard/view-updates" replace />;
  }
  return <DashboardHome />;
};

function App() {
  const userId = localStorage.getItem('userId');
  return (
    <SnackbarProvider
      maxSnack={3}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      autoHideDuration={6000}
      action={(snackbarId) => (
        <IconButton
          size="small"
          aria-label="close"
          color="inherit"
          onClick={() => closeSnackbar(snackbarId)}
          sx={{ p: 0.5 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    >
      <ProcessProvider>
        <NotificationProvider>
          <FavoritesProvider>
            <ActivityLogProvider>
              <PermissionProvider>
              <Router>
            <Routes>
              {/* Public (unauthenticated) routes */}
              <Route path="/" element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              } />
              <Route path="/2fa" element={
                <Protected2FARoute>
                  <TwoFactorAuthentication />
                </Protected2FARoute>
              } />
              <Route path="/reset-password" element={
                <PublicRoute>
                  <ResetPasswordPage />
                </PublicRoute>
              } />

              {/* Protected dashboard and nested routes */}
              <Route path="/dashboard" element={
                <ProtectedRoute key={localStorage.getItem('isLoggedIn')}>
                  <Dashboard />
                </ProtectedRoute>
              } >
                <Route index element={<DashboardLanding />} />
                <Route path="view-updates" element={<ViewUpdates />} />
                <Route path="pdf-manipulation" element={<ProtectedInterfaceRoute><PDFManipulation /></ProtectedInterfaceRoute>} />
                {/* <Route path="furniture-testing" element={<ProtectedInterfaceRoute><FurnitureTesting /></ProtectedInterfaceRoute>} /> */}{/* DISABLED - Interface hidden */}
                {/* <Route path="product-building" element={<ProtectedInterfaceRoute><ProductBuilding /></ProtectedInterfaceRoute>} /> */}{/* DISABLED - Interface hidden */}
                <Route path="excel-reports" element={<ProtectedInterfaceRoute><ExcelReports /></ProtectedInterfaceRoute>} />
                <Route path="pdf-reports" element={<ProtectedInterfaceRoute><ExcelReports sourceFilter="pdf" /></ProtectedInterfaceRoute>} />
                <Route path="saf1909-inventory-analysis" element={<ProtectedInterfaceRoute><SAF1909InventoryAnalysis /></ProtectedInterfaceRoute>} />
                <Route path="remove-background" element={<ProtectedInterfaceRoute><RemoveBackground /></ProtectedInterfaceRoute>} />
                <Route path="price-scraper" element={<ProtectedInterfaceRoute><PriceScraper /></ProtectedInterfaceRoute>} />
                {/* <Route path="ftp-transfer" element={<ProtectedInterfaceRoute><FTPTransfer /></ProtectedInterfaceRoute>} /> */}{/* DISABLED - Interface hidden */}
                <Route path="marketing-copies" element={<ProtectedInterfaceRoute><MarketingCopies /></ProtectedInterfaceRoute>} />
                {/* <Route path="inactive-items" element={<ProtectedInterfaceRoute><InactiveItems /></ProtectedInterfaceRoute>} /> */}{/* DISABLED - Interface hidden */}
                <Route path="48-state-freight" element={<ProtectedInterfaceRoute><StateFreight /></ProtectedInterfaceRoute>} />
                <Route path="document-translation" element={<ProtectedInterfaceRoute><DocumentTranslation /></ProtectedInterfaceRoute>} />
                <Route path="product-details" element={<ProtectedInterfaceRoute><ProductDetails /></ProtectedInterfaceRoute>} />
                <Route path="excel-export-item-images" element={<ProtectedInterfaceRoute><ExportImages /></ProtectedInterfaceRoute>} />
                <Route path="item-image-grouping" element={<ProtectedInterfaceRoute><ItemImageGrouping /></ProtectedInterfaceRoute>} />
                <Route path="aws-file-links" element={<ProtectedInterfaceRoute><AWSImageLinks /></ProtectedInterfaceRoute>} />
                {/* <Route path="aws-image-links-jeff" element={<ProtectedInterfaceRoute><AWSImageLinksJeff /></ProtectedInterfaceRoute>} /> */}
                {/* <Route path="jeff-furniture" element={<ProtectedInterfaceRoute><AWSImageLinksJeffFurniture /></ProtectedInterfaceRoute>} /> */}
                <Route path="aws-file-links-alternate" element={<ProtectedInterfaceRoute><AWSImageLinksAlternate /></ProtectedInterfaceRoute>} />
                <Route path="collection-image-links" element={<ProtectedInterfaceRoute><CollectionImageLinks /></ProtectedInterfaceRoute>} />
                <Route path="wayfair-asset-upload" element={<ProtectedInterfaceRoute><WayfairAssetUpload /></ProtectedInterfaceRoute>} />
                <Route path="amazon-bulk-image-uploader" element={<ProtectedInterfaceRoute><AmazonBulkImageUploader /></ProtectedInterfaceRoute>} />
                <Route path="broken-link-checker" element={<ProtectedInterfaceRoute><BrokenLinkChecker /></ProtectedInterfaceRoute>} />
                <Route path="social-media-content" element={<ProtectedInterfaceRoute><SocialMediaContent /></ProtectedInterfaceRoute>} />
                <Route path="product-data-validator" element={<ProtectedInterfaceRoute><ProductDataValidator /></ProtectedInterfaceRoute>} />
                <Route path="seo-content-writer" element={<ProtectedInterfaceRoute><SEOContentWriter /></ProtectedInterfaceRoute>} />
                <Route path="email-campaign-builder" element={<ProtectedInterfaceRoute><EmailCampaignBuilder /></ProtectedInterfaceRoute>} />
                <Route path="barcode-generator" element={<ProtectedInterfaceRoute><BarcodeGenerator /></ProtectedInterfaceRoute>} />
                <Route path="customer-style-search" element={<ProtectedInterfaceRoute><CustomerStyles /></ProtectedInterfaceRoute>} />
                <Route path="items-detail" element={<ProtectedInterfaceRoute><ItemsDetail /></ProtectedInterfaceRoute>} />
                <Route path="excel-tools" element={<ExcelLanding />} />
                <Route path="pdf-tools" element={<PDFLanding />} />
                <Route path="link-tools" element={<LinksLanding />} />
                <Route path="other-tools" element={<OthersLanding />} />
                <Route path="ai-tools" element={<AILanding />} />
                <Route path="analytics-tools" element={<ReportsLanding />} />
                <Route path="reports-tools" element={<Navigate to="/dashboard/analytics-tools" replace />} />
                <Route path="ai-image-generator" element={<ProtectedInterfaceRoute><AIImageGenerator /></ProtectedInterfaceRoute>} />
                <Route path="ai-video-generator" element={<ProtectedInterfaceRoute><AIVideoGenerator /></ProtectedInterfaceRoute>} />
                <Route path="ai-image-search" element={<ProtectedInterfaceRoute><AIImageSearch /></ProtectedInterfaceRoute>} />
                <Route path="rug-room-shots" element={<ProtectedInterfaceRoute><RugRoomShots /></ProtectedInterfaceRoute>} />
                <Route path="ai-photo-pipeline" element={<Navigate to="/dashboard" replace />} /> {/* retired 2026-10-19 */}
                <Route path="amazon-rug-titles" element={<ProtectedInterfaceRoute><AmazonRugTitles /></ProtectedInterfaceRoute>} />
                <Route path="sales-performance-dashboard" element={<ProtectedInterfaceRoute><SalesPerformanceDashboard /></ProtectedInterfaceRoute>} />
                <Route path="sales-dashboard-v2" element={<ProtectedInterfaceRoute><SalesDashboardV2 /></ProtectedInterfaceRoute>} />
                <Route path="returns-dashboard" element={<ProtectedInterfaceRoute><ReturnsDashboard /></ProtectedInterfaceRoute>} />
                <Route path="retail-os" element={<ProtectedInterfaceRoute><RetailOS /></ProtectedInterfaceRoute>} />
                <Route path="new-items" element={<ProtectedInterfaceRoute><NewItems /></ProtectedInterfaceRoute>} />
                <Route path="customer-style-gaps" element={<ProtectedInterfaceRoute><CustomerStyleGaps /></ProtectedInterfaceRoute>} />
                <Route path="collection-launches" element={<ProtectedInterfaceRoute><CollectionLaunches /></ProtectedInterfaceRoute>} />
                <Route path="sales-pulse" element={<ProtectedInterfaceRoute><SalesPulse /></ProtectedInterfaceRoute>} />
                {/* Reorder Console: per-user grant (Ryan) + admins/owners; the API's ReorderConsoleAccess filter is the real gate */}
                <Route path="reorder-console" element={<ProtectedInterfaceRoute><ReorderConsole /></ProtectedInterfaceRoute>} />
                <Route path="sales-dashboard" element={<ProtectedInterfaceRoute><SalesDashboard /></ProtectedInterfaceRoute>} />
                <Route path="home-goods-pdf" element={<ProtectedInterfaceRoute><HomeGoods /></ProtectedInterfaceRoute>} />
                <Route path="pallet-labels" element={<ProtectedInterfaceRoute><PalletLabels /></ProtectedInterfaceRoute>} />
                <Route path="pdq-full-pallet-pull-label" element={<ProtectedInterfaceRoute><PDQLabels /></ProtectedInterfaceRoute>} />
                <Route path="po-distribution" element={<ProtectedInterfaceRoute><PODistribution /></ProtectedInterfaceRoute>} />
                <Route path="po-distribution-whs" element={<ProtectedInterfaceRoute><PODistributionWHS /></ProtectedInterfaceRoute>} />
                <Route path="canadian-13-provinces-freight" element={<ProtectedInterfaceRoute><CanadianFreight /></ProtectedInterfaceRoute>} />
                <Route path="eu-freight" element={<ProtectedInterfaceRoute><EUFreight /></ProtectedInterfaceRoute>} />
                <Route path="reports-scheduler" element={<ProtectedInterfaceRoute><ReportsScheduler /></ProtectedInterfaceRoute>} />
                <Route path="pallet-optimizer" element={<ProtectedInterfaceRoute><PalletOptimizer /></ProtectedInterfaceRoute>} />
                <Route path="bilingual-labels" element={<ProtectedInterfaceRoute><BilingualLabels /></ProtectedInterfaceRoute>} />
                <Route path="ama9999-ucc-labels" element={<ProtectedInterfaceRoute><AMA9999UCCLabels /></ProtectedInterfaceRoute>} />
                <Route path="expense-allocation-invoices" element={<ProtectedInterfaceRoute><SafaviehInvoices /></ProtectedInterfaceRoute>} />
                <Route path="carton-labels" element={<ProtectedInterfaceRoute><CartonLabels /></ProtectedInterfaceRoute>} />
                <Route path="amazon-di-ucc-label" element={<ProtectedInterfaceRoute><AmazonLabelSeparator /></ProtectedInterfaceRoute>} />
                <Route path="pdf-to-excel-ucc-labels" element={<ProtectedInterfaceRoute><PDFtoExcel /></ProtectedInterfaceRoute>} />
                <Route path="cad-invoices" element={<ProtectedInterfaceRoute><CADInvoices /></ProtectedInterfaceRoute>} />
                <Route path="master-retail-rugs" element={<ProtectedInterfaceRoute><MasterRetailRugs /></ProtectedInterfaceRoute>} />
                <Route path="label-printing" element={<ProtectedInterfaceRoute><LabelPrinting /></ProtectedInterfaceRoute>} />
                <Route path="sales-orders-export" element={<ProtectedInterfaceRoute><SalesOrderCreation /></ProtectedInterfaceRoute>} />
                <Route path="asset-explorer" element={<ProtectedInterfaceRoute><AssetExplorer /></ProtectedInterfaceRoute>} />
                <Route path="bulk-image-downloader" element={<ProtectedInterfaceRoute><BulkImageDownloader /></ProtectedInterfaceRoute>} />
                <Route path="file-converter" element={<ProtectedInterfaceRoute><FileConverter /></ProtectedInterfaceRoute>} />
                {/* Admin routes */}
                <Route path="admin/users" element={<ProtectedInterfaceRoute><UserManagement /></ProtectedInterfaceRoute>} />
                <Route path="admin/roles" element={<ProtectedInterfaceRoute><RoleManagement /></ProtectedInterfaceRoute>} />
                <Route path="admin/notifications" element={<ProtectedInterfaceRoute><NotificationManagement /></ProtectedInterfaceRoute>} />
                <Route path="admin/file-history" element={<ProtectedInterfaceRoute><FileHistoryAdmin /></ProtectedInterfaceRoute>} />
                {/* Workspace (personal productivity hub) */}
                <Route path="workspace" element={<WorkspacePage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="tasks" element={<TasksPage />} />
                <Route path="password-generator" element={<PasswordGeneratorPage />} />
                {/* Catch-all for unknown dashboard routes - redirects to dashboard home */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Route>

              {/* Fallback route (optional) */}
              <Route path="*" element={<div>404 Not Found</div>} />
            </Routes>
              </Router>
              <ActivityLogPanel />
              </PermissionProvider>
            </ActivityLogProvider>
          </FavoritesProvider>
        </NotificationProvider>
      </ProcessProvider>
    </SnackbarProvider>
  );
}

export default App;